import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const LoadingAnimation = ({ onComplete }) => {
  const [loadingStage, setLoadingStage] = useState(0);
  const [progress, setProgress] = useState(0);

  const loadingStages = [
    { text: "Where Code Meets Innovation", icon: "Box" },
    { text: "Bringing Your Ideas to Life", icon: "Zap" },
    { text: "Dream. Design. Develop. Deliver.", icon: "Play" },
    { text: "Your Digital Journey Starts Here", icon: "Gauge" },
    { text: "Ready to Explore", icon: "Rocket" }
  ];

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => onComplete(), 500);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    const stageInterval = setInterval(() => {
      setLoadingStage(prev => {
        if (prev >= loadingStages?.length - 1) {
          clearInterval(stageInterval);
          return prev;
        }
        return prev + 1;
      });
    }, 1000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(stageInterval);
    };
  }, [onComplete]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.5 }}
        className="fixed inset-0 bg-background z-50 flex items-center justify-center"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, var(--color-accent) 1px, transparent 0)`,
            backgroundSize: '50px 50px',
          }}></div>
        </div>

        <div className="relative z-10 text-center max-w-md mx-auto px-6">
          {/* Animated Logo */}
          <motion.div
            initial={{ scale: 0, rotateY: -180 }}
            animate={{ scale: 1, rotateY: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="mb-8"
          >
            <div className="relative group perspective-1000 mx-auto w-20 h-20">
              <motion.div
                animate={{
                  rotateY: [0, 360],
                  rotateX: [0, 15, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "linear"
                }}
                // className="w-20 h-20 bg-gradient-to-br from-accent to-accent/70 rounded-2xl shadow-accent transform-3d"
              >
                <div className="absolute inset-0 bg-primary/20 rounded-2xl transform rotate-6"></div>
                <div className="absolute inset-4 bg-accent rounded-xl flex items-center justify-center">

                  <img
                    src="/logo.jpeg"
                    alt="shutter-lifters"
                    className="w-[150px] h-[150px]"
                    style={{ color: "var(--color-primary)" ,borderRadius:"5px"}}
                  />
                </div>
              </motion.div>

              {/* Orbiting Particles */}
              {[...Array(6)]?.map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-accent/60 rounded-full"
                  animate={{
                    x: [0, Math.cos(i * 60) * 50, 0],
                    y: [0, Math.sin(i * 60) * 50, 0],
                    opacity: [0.3, 1, 0.3],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                  style={{
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              ))}
            </div>
          </motion.div>

          {/* Brand Name */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-3xl font-bold text-foreground mb-2"
          >
            <span className="bg-gradient-to-r from-accent to-accent/70 bg-clip-text text-transparent">
              Shutter Lifters
            </span>
          </motion.h1>

          {/* Loading Stage */}
          <motion.div
            key={loadingStage}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="flex items-center justify-center space-x-3 mb-4">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-6 h-6 bg-accent/20 rounded-lg flex items-center justify-center"
              >
                <Icon
                  name={loadingStages?.[loadingStage]?.icon}
                  size={16}
                  className="text-accent"
                />
              </motion.div>
              <span className="text-muted-foreground font-medium">
                {loadingStages?.[loadingStage]?.text}
              </span>
            </div>
          </motion.div>

          {/* Progress Bar */}
          <div className="relative mb-6">
            <div className="w-full h-2 bg-muted/30 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-accent to-accent/70 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </div>

            {/* Progress Percentage */}
            <motion.div
              className="absolute -top-8 text-sm font-medium text-accent"
              style={{ left: `${progress}%`, transform: 'translateX(-50%)' }}
              animate={{ left: `${progress}%` }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {Math.round(progress)}%
            </motion.div>
          </div>

          {/* Loading Dots */}
          <div className="flex justify-center space-x-2">
            {[...Array(3)]?.map((_, i) => (
              <motion.div
                key={i}
                className="w-2 h-2 bg-accent/60 rounded-full"
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>

          {/* Completion Message */}
          {progress === 100 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-6"
            >
              <div className="flex items-center justify-center space-x-2 text-accent">
                <Icon name="CheckCircle" size={20} />
                <span className="font-medium">Experience Ready!</span>
              </div>
            </motion.div>
          )}
        </div>

        {/* Floating Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(8)]?.map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-accent/20 rounded-full"
              animate={{
                x: [0, Math.random() * window.innerWidth],
                y: [0, Math.random() * window.innerHeight],
                opacity: [0, 0.5, 0],
              }}
              transition={{
                duration: Math.random() * 10 + 5,
                repeat: Infinity,
                delay: i * 0.5,
              }}
              style={{
                left: Math.random() * window.innerWidth,
                top: Math.random() * window.innerHeight,
              }}
            />
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LoadingAnimation;